import React, { useState, useEffect, useRef } from 'react';
import { useStripe } from '@stripe/react-stripe-js';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import styled from 'styled-components';
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../store/store";
import { updateUser } from "../reducers/authSlice";
import api from "../api/axiosInstance";
import NotificationModal from './NotificationModal';

const MessageContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100vh;
    text-align: center;
`;

const LoadingIcon = styled(FaSpinner)`
    color: #007bff;
    font-size: 4rem;
    margin-bottom: 20px;
    animation: spin 1s linear infinite;

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;

const SuccessIcon = styled(FaCheckCircle)`
    color: green;
    font-size: 4rem;
    margin-bottom: 20px;
`;

const ErrorIcon = styled(FaTimesCircle)`
    color: red;
    font-size: 4rem;
    margin-bottom: 20px;
`;

const MessageText = styled.p`
    font-size: 1.5rem;
    margin: 0;
`;

const PaymentStatus: React.FC = () => {
    const stripe = useStripe();
    const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
    const [message, setMessage] = useState<string>('Spracovávam platbu...');
    const user = useSelector((state: RootState) => state.auth.user);
    const dispatch = useDispatch();
    const [isModalOpen, setModalOpen] = useState<boolean>(false); // Modal visibility state
    
    // Ref to prevent duplicate subscription creation
    const isProcessingRef = useRef(false);
    const processedIntentRef = useRef<string | null>(null);

    useEffect(() => {
        const checkPaymentStatus = async () => {
            if (!stripe) return;

            const urlParams = new URLSearchParams(window.location.search);
            
            // Check if this is a PaymentIntent return (one-time payment)
            const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret');
            const setupIntentClientSecret = urlParams.get('setup_intent_client_secret');
            const productIdParam = urlParams.get('productId');
            const productId = productIdParam ? parseInt(productIdParam, 10) : 1;

            // Handle PaymentIntent return (one-time payments)
            if (paymentIntentClientSecret) {
                if (processedIntentRef.current === paymentIntentClientSecret) return;
                if (isProcessingRef.current) return;

                const { paymentIntent, error } = await stripe.retrievePaymentIntent(paymentIntentClientSecret);

                if (error || !paymentIntent) {
                    setStatus('error');
                    setMessage('Nepodarilo sa načítať platbu.');
                    return;
                }

                switch (paymentIntent.status) {
                    case 'succeeded':
                        isProcessingRef.current = true;
                        processedIntentRef.current = paymentIntentClientSecret;
                        
                        try {
                            // For one-time payments, the webhook will handle premium activation
                            // But we also update the user here for immediate feedback
                            const updatedUser = await api.put(`/account/premium/${user?.id}?productId=${productId}`, {});
                            await api.post(`/orders/create?userId=${user?.id}`, {});
                            dispatch(updateUser(updatedUser.data));
                            setStatus('success');
                            setMessage('Platba prebehla úspešne!');
                            setModalOpen(true);
                        } catch (err: any) {
                            // 402 = the server could not yet match the payment in Stripe. The webhook still
                            // activates the membership, so treat it like 409 rather than alarming a
                            // customer whose card was charged.
                            if (err?.response?.status === 409 || err?.response?.status === 402) {
                                setStatus('success');
                                setMessage('Platba prebehla úspešne!');
                                setModalOpen(true);
                            } else {
                                setStatus('error');
                                setMessage('Chyba pri aktivácii prémia.');
                                isProcessingRef.current = false;
                                processedIntentRef.current = null;
                            }
                        }
                        break;

                    case 'requires_payment_method':
                        setStatus('error');
                        setMessage('Platba sa nepodarila, skúste znova.');
                        break;

                    case 'processing':
                        setStatus('loading');
                        setMessage('Platba sa spracováva...');
                        break;

                    default:
                        setStatus('loading');
                        setMessage('Čaká sa na dokončenie...');
                        break;
                }
                return;
            }

            // Handle SetupIntent return (recurring subscriptions)
            if (setupIntentClientSecret) {
                if (processedIntentRef.current === setupIntentClientSecret) return;
                if (isProcessingRef.current) return;

                const { setupIntent, error } = await stripe.retrieveSetupIntent(setupIntentClientSecret);

                if (error || !setupIntent) {
                    setStatus('error');
                    setMessage('Nepodarilo sa načítať SetupIntent.');
                    return;
                }

                switch (setupIntent.status) {
                    case 'succeeded':
                        isProcessingRef.current = true;
                        processedIntentRef.current = setupIntentClientSecret;
                        
                        try {
                            const pmId = setupIntent.payment_method;
                            
                            await api.post(`/subscription/${user?.id}`, { 
                                paymentMethodId: pmId,
                                productId: productId
                            });
                            
                            const updatedUser = await api.put(`/account/premium/${user?.id}?productId=${productId}`, {});
                            await api.post(`/orders/create?userId=${user?.id}`, {});
                            dispatch(updateUser(updatedUser.data));
                            setStatus('success');
                            setMessage('Platba prebehla úspešne!');
                            setModalOpen(true);
                        } catch (err: any) {
                            // 402 = the server could not yet match the payment in Stripe. The webhook still
                            // activates the membership, so treat it like 409 rather than alarming a
                            // customer whose card was charged.
                            if (err?.response?.status === 409 || err?.response?.status === 402) {
                                setStatus('success');
                                setMessage('Platba prebehla úspešne!');
                                setModalOpen(true);
                            } else {
                                setStatus('error');
                                setMessage('Chyba pri vytváraní predplatného.');
                                isProcessingRef.current = false;
                                processedIntentRef.current = null;
                            }
                        }
                        break;

                    case 'requires_payment_method':
                        setStatus('error');
                        setMessage('Platba sa nepodarila, skúste znova.');
                        break;

                    default:
                        setStatus('loading');
                        setMessage('Čaká sa na dokončenie...');
                        break;
                }
                return;
            }

            // No valid client secret found
            setStatus('error');
            setMessage('Chýba informácia o platbe.');
        };

        checkPaymentStatus();
    }, [stripe, user?.id, dispatch]);

    const handleSettingsUpdate = (data: any) => {
        // Handle any updates when settings are changed in the modal
    };

    const handleCloseModal = () => {
        setModalOpen(false); // Close the modal
    };

    return (
        <>
            <MessageContainer>
                {status === 'loading' && <LoadingIcon />}
                {status === 'success' && <SuccessIcon />}
                {status === 'error' && <ErrorIcon />}
                <MessageText>{message}</MessageText>
            </MessageContainer>

            <NotificationModal
                isOpen={isModalOpen}
                onRequestClose={handleCloseModal}
                onSettingsUpdate={handleSettingsUpdate}
            />
        </>
    );
};

export default PaymentStatus;
